'use client';

import { Box, alpha, useTheme } from '@mui/material';
import {
  FaceEventActions,
  FaceLivenessDetailType,
  FaceLivenessWebComponent,
  IFaceLiveness,
} from '@regulaforensics/vp-frontend-face-components';
import { stringUtils, useFeatureToggle, useMobile } from '@verifime/utils';
import { DetailedHTMLProps, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import CheckError from './CheckError';
import CheckSuccess from './CheckSuccess';
import NoCamera from './NoCamera';
import ReachedMaxTries from './ReachedMaxTries';
import SkippingFaceCheck from './SkippingFaceCheck';
import StartScreen from './StartScreen';

type ErrorTypes = any;
type TLivenessCheckStatus = 'SUCCEEDED' | 'FAILED';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'face-liveness': DetailedHTMLProps<
        IFaceLiveness & HTMLAttributes<FaceLivenessWebComponent>,
        FaceLivenessWebComponent
      >;
    }
  }
}

export default function FaceLiveness({
  url,
  sessionId,
  token,
  maxTryCount = Number.MAX_VALUE,
  customisedReachMaxTries,
  customisedSuccess,
  customisedSkip,
  isEnableOptions = true,
  isShowQrCodeWhenNoCamera = false,
  onFinish,
  onSuccess,
  onNoCamera,
  onReachMaxTries,
  onSkip,
  onRetry,
  onStartButtonClick,
}: Readonly<{
  url: string;
  sessionId: string;
  token: string;
  customisedSkip?: ReactNode;
  isEnableOptions?: boolean;
  onSkip?: () => void;
  maxTryCount?: number;
  customisedReachMaxTries?: ReactNode;
  customisedSuccess?: ReactNode;
  isShowQrCodeWhenNoCamera?: boolean;
  // Finished the face check
  onFinish?: (transactionId: string, status: TLivenessCheckStatus) => void;
  // The face check succeedeed
  onSuccess?: () => void;
  onNoCamera?: () => void;
  onReachMaxTries?: () => void;
  onRetry?: () => void;
  onStartButtonClick?: () => void;
}>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSkip, setIsSkip] = useState(false);
  const [errorType, setErrorType] = useState<ErrorTypes | null>(null);
  const [triedCount, setTriedCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef<FaceLivenessWebComponent>(null);
  const actionHistoryRef = useRef<FaceEventActions[]>([]);
  const theme = useTheme();
  const isMobile = useMobile();

  const { isSyncUploadFaceCheckVideo, isPassiveFaceCheckEnabled } = useFeatureToggle();

  useEffect(() => {
    import('@regulaforensics/vp-frontend-face-components');
  }, []);

  const isReachedMaxTries = triedCount >= maxTryCount;

  useEffect(() => {
    if (isReachedMaxTries) {
      onReachMaxTries?.();
    }
  }, [onReachMaxTries, isReachedMaxTries]);

  const listener = (data: CustomEvent<FaceLivenessDetailType>) => {
    actionHistoryRef.current.push(data.detail.action);

    if (data.detail.action === 'PROCESS_FINISHED') {
      const { status: dataStatus, response, reason } = data.detail.data;
      // Errors happen
      if (dataStatus === 0) {
        setErrorType(reason as ErrorTypes);
      }

      const invalidResponseCodes = stringUtils.parsePart(
        process.env.NEXT_PUBLIC_FACE_SDK_INVALID_RESPONSE_CODES,
      );

      if (invalidResponseCodes.includes(response?.code)) {
        console.warn('Invalid response code received:', response?.code);
        return;
      }

      // As required, if there are images in the response, we consider it as a successful face check.
      if (response && response.images?.length > 0) {
        const { transactionId } = data.detail.data.response;

        const checkStatus = 'SUCCEEDED'; // Always consider it as succeeded, as we don't consider the status in the response but there are images in the response.
        onFinish?.(transactionId, checkStatus);
        setTriedCount((triedCount) => triedCount + 1);
        onSuccess?.();
        setIsSuccess(true);
      }
    }

    if (actionHistoryRef.current.length > 1) {
      const [secondLastAction, lastAction] = actionHistoryRef.current.slice(-2);
      // In these unexpected actions will show Regula default `Selfie time` screen.
      // We just close this screen directly.
      if (
        (secondLastAction === 'PROCESS_FINISHED' && lastAction === 'ELEMENT_VISIBLE') ||
        (secondLastAction === 'ELEMENT_VISIBLE' && lastAction === 'ELEMENT_VISIBLE')
      ) {
        setIsOpen(false);
        actionHistoryRef.current = [];
      }
    }

    if (data.detail?.action === 'CLOSE' || data.detail?.action === 'RETRY_COUNTER_EXCEEDED') {
      setIsOpen(false);
      actionHistoryRef.current = [];
    }
  };

  useEffect(() => {
    if (isOpen && componentRef.current) {
      componentRef.current.settings = {
        url,
        headers: {
          Authorization: 'Bearer ' + token,
        },
        recordingProcess: isSyncUploadFaceCheckVideo ? 1 : 0, // 1 for upload video with data stream (problematic as video stream must be completed successfully), 0 for async video upload (video stream is not critical)
        livenessType: isPassiveFaceCheckEnabled ? 1 : 0, // 1 for passive face check, 0 for active face check
        copyright: false,
        tag: sessionId,
        startScreen: false,
        finishScreen: true,
        customization: {
          retryScreenRetryButtonBackground: theme.palette.primary.main,
          retryScreenRetryButtonBackgroundHover: alpha(theme.palette.primary.main, 0.8),
        },
      };

      // To set the face-liveness 100% width
      setTimeout(() => {
        const divContainer = componentRef.current.shadowRoot.querySelector('div');
        divContainer?.setAttribute('style', 'width:100%');
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const containerCurrent = containerRef.current;

    if (!containerCurrent) return;

    containerCurrent.addEventListener('face-liveness', listener);

    return () => {
      containerCurrent.removeEventListener('face-liveness', listener);
    };
  }, []);

  const OpenedFaceCheckComponent = () => {
    if (['CAMERA_PERMISSION_DENIED', 'NO_CAMERA', 'CAMERA_UNKNOWN_ERROR'].includes(errorType)) {
      onNoCamera?.();
      return (
        <NoCamera
          onSkipConfirm={() => {
            setIsSkip(true);
            onSkip?.();
          }}
          isEnableOptions={isEnableOptions}
          isShowQrCodeWhenNoCamera={isShowQrCodeWhenNoCamera}
        />
      );
    }

    if (['WASM_ERROR', 'UNKNOWN_ERROR', 'NOT_SUPPORTED', 'CONNECTION_ERROR'].includes(errorType)) {
      return (
        <CheckError
          onSkip={() => {
            setIsSkip(true);
            onSkip?.();
          }}
          isEnableSkip={isEnableOptions}
        />
      );
    }

    if (isSuccess) {
      return <CheckSuccess customisedSuccess={customisedSuccess} />;
    }

    if (isReachedMaxTries) {
      return <ReachedMaxTries customisedReachMaxTries={customisedReachMaxTries} />;
    }

    if (isOpen) {
      return (
        <face-liveness
          ref={componentRef}
          style={
            isMobile
              ? {
                  height: '100vh',
                  position: 'absolute',
                  zIndex: 2,
                  top: 0,
                  left: 0,
                  right: 0,
                  width: '100vw',
                  bottom: 0,
                }
              : {}
          }
        ></face-liveness>
      );
    }
  };

  const FaceCheckComponent = () => {
    if (isSkip) {
      return (
        <SkippingFaceCheck
          customisedSkip={customisedSkip}
          onRetry={() => {
            setIsOpen(true);
            setIsSkip(false);
            onRetry?.();
          }}
        />
      );
    }

    if (isOpen) {
      return <OpenedFaceCheckComponent />;
    }

    return (
      <StartScreen
        isEnableOptions={isEnableOptions}
        onButtonClick={() => {
          setIsOpen(true);
          onStartButtonClick?.();
        }}
        onSkipConfirm={() => {
          setIsSkip(true);
          onSkip?.();
        }}
      />
    );
  };

  return (
    <Box ref={containerRef}>
      <FaceCheckComponent />
    </Box>
  );
}
